/**
 * Vista 3 · Panel de indicadores.
 *
 * `ARQ-9`: los indicadores se calculan EN LA PETICIÓN, no por un planificador.
 * «Vence en los próximos 3 días hábiles» es una función de la fecha de hoy, y
 * como tal no puede quedarse obsoleta ni fallar en silencio de madrugada.
 */
import { Button, ProgressBar, StatusBadge } from '@mpfn/ui'
import { Callout, DataTable, EmptyState, PageHeader, StatCard, type DataTableColumn } from '@mpfn/patterns'
import { Inbox } from 'lucide-react'
import { useBloques, usePanel } from '../api/consultas.js'
import { etiquetaDe, useCatalogos } from '../api/catalogos.js'
import type { Bloque, Indicadores } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { Enlace, irA } from '../navegacion/Enlace.js'
import { usePuede } from '../sesion/contexto.js'
import { numero, periodo as formatearPeriodo } from '../lib/formato.js'
import { plural } from '../lib/avisos.js'

export function Panel() {
  const indicadores = usePanel()
  const bloques = useBloques()
  const catalogos = useCatalogos()
  const puedeSortear = usePuede('sorteo.ejecutar')

  const columnas: ReadonlyArray<DataTableColumn<Bloque>> = [
    { id: 'periodo', header: 'Período', cell: (fila) => formatearPeriodo(fila.periodo) },
    { id: 'etiqueta', header: 'Etiqueta', cell: (fila) => fila.etiqueta, hideOnMobile: true },
    {
      id: 'estado',
      header: 'Estado',
      cell: (fila) => (
        <StatusBadge
          tone={fila.estado === 'en_fiscalizacion' ? 'info' : 'neutral'}
          label={etiquetaDe(catalogos.estadosBloque, fila.estado)}
        />
      ),
    },
  ]

  return (
    <Pagina>
      <Datos estado={indicadores}>
        {(datos) => <CabeceraYKpis datos={datos} />}
      </Datos>

      <section className="sigfisc-seccion">
        <h2 className="sigfisc-seccion__titulo">Bloques</h2>
        <Datos estado={bloques}>
          {(lista) =>
            lista.length === 0 ? (
              <EmptyState
                icon={Inbox}
                title="Aún no hay bloques de fiscalización"
                description="Un bloque se abre cargando el padrón de altas del mes y generando la selección de muestra."
                {...(puedeSortear
                  ? {
                      action: (
                        <Button
                          variant="primary"
                          onClick={() => {
                            irA('sorteo')
                          }}
                        >
                          Ir a la selección de muestra
                        </Button>
                      ),
                    }
                  : {})}
              />
            ) : (
              <DataTable
                columns={columnas}
                rows={lista}
                rowKey={(fila) => fila.id}
                onRowClick={(fila) => {
                  irA('bloque', { periodo: fila.periodo })
                }}
                rowActionLabel={(fila) => `Abrir el bloque ${formatearPeriodo(fila.periodo)}`}
                empty={{ title: 'Sin bloques' }}
              />
            )
          }
        </Datos>
      </section>
    </Pagina>
  )
}

function CabeceraYKpis({ datos }: { datos: Indicadores }) {
  if (!datos.bloque) {
    return (
      <PageHeader
        title="Panel de indicadores"
        description="Todavía no hay un bloque en fiscalización."
      />
    )
  }

  const avisos = [
    datos.porVencer > 0 &&
      `${plural(datos.porVencer, 'consulta vence', 'consultas vencen')} en los próximos 3 días hábiles.`,
    datos.decisionRequerida > 0 &&
      `${plural(datos.decisionRequerida, 'requerimiento espera', 'requerimientos esperan')} decisión.`,
    datos.pendientesDerivar > 0 &&
      `${plural(datos.pendientesDerivar, 'documento está pendiente', 'documentos están pendientes')} de derivar al OCI.`,
  ].filter((aviso): aviso is string => typeof aviso === 'string')

  return (
    <>
      <PageHeader
        title={`Bloque ${formatearPeriodo(datos.bloque.periodo)}`}
        description={`${numero(datos.documentos)} documentos declarados en fiscalización.`}
        action={
          // `asChild` monta el enlace CON el aspecto de botón: sigue siendo un
          // `<a href>` que se puede copiar y abrir en otra pestaña.
          <Button variant="primary" asChild>
            <Enlace vista="bloque" parametros={{ periodo: datos.bloque.periodo }}>
              Ir al bloque
            </Enlace>
          </Button>
        }
      />

      <div className="sigfisc-kpis">
        <StatCard label="Documentos" value={numero(datos.documentos)} />
        <StatCard
          label="Verificados"
          value={numero(datos.verificados)}
          detail={
            datos.documentos > 0 ? (
              <ProgressBar
                value={Math.round((datos.verificados / datos.documentos) * 100)}
                label="Avance de verificación"
              />
            ) : undefined
          }
        />
        <StatCard label="En proceso" value={numero(datos.enProceso)} />
        <StatCard
          label="Irregulares"
          value={numero(datos.irregulares)}
          detail={
            datos.noAcreditados > 0 ? `${numero(datos.noAcreditados)} no acreditados` : undefined
          }
        />
      </div>

      {avisos.length > 0 && (
        <Callout
          tone={datos.pendientesDerivar > 0 ? 'critical' : 'warning'}
          title="Requieren atención"
          action={
            <Button variant="secondary" size="sm" asChild>
              <Enlace vista="bloque" parametros={{ periodo: datos.bloque.periodo }}>
                Ver en el bloque
              </Enlace>
            </Button>
          }
        >
          <ul className="sigfisc-avisos">
            {avisos.map((aviso) => (
              <li key={aviso}>{aviso}</li>
            ))}
          </ul>
        </Callout>
      )}
    </>
  )
}
